import type { QueryClient } from '@tanstack/react-query';
import type {
  BroadcastTrigger as BroadcastTriggerWire,
  CampaignsCampaignModel,
  CampaignsListCampaignsResponse,
} from '@customerio/hydra-ui-api';
import { actionKeys, parseAction, type Action } from '../action/entity';
import { parseBroadcastTrigger, type BroadcastTrigger } from '../broadcast-trigger/entity';
import { parseSubscriptionTopic, subscriptionTopicKeys, type SubscriptionTopic } from '../subscription-topic/entity';
import { parseTag, tagKeys, type Tag } from '../tag/entity';
import { campaignKeys, parseCampaign, type Campaign } from './entity';

/**
 * Cluster propagation helpers.
 *
 * `populateCampaignModel` is the cluster-shape sideload helper used by:
 *  - `getCampaign(id)` / `getCampaignWorkflowDetail(id)`'s queryFns
 *    (read-side propagation)
 *  - every CampaignModel-returning mutation's `onSuccess`
 *    (`addCampaignActions`, `deleteCampaignActions`, `createSplitTest`,
 *    `selectSplitWinner`)
 *
 * Besides the per-entity detail slots it also writes the campaign's
 * workflow-detail slot (`campaignKeys.workflowDetail`), so the workflow
 * canvas sees every CampaignModel the app receives.
 *
 * `populateCampaignsList` handles the LIST response shape — multiple
 * campaigns with sibling-array sideloads grouped at the top level. Its
 * action rows are the sparse list shape, so it writes detail slots only
 * and never the workflow-detail slot.
 */

export interface ParsedCampaignModel {
  campaign: Campaign;
  actions: readonly Action[];
  tags: readonly Tag[];
  subscription_topics: readonly SubscriptionTopic[];
  /**
   * An API-triggered broadcast's latest trigger. Only `GET /campaigns/:id`
   * sends it, and only once the broadcast has been triggered; `undefined`
   * means this response doesn't say.
   */
  most_recent_trigger?: BroadcastTrigger;
}

/**
 * A campaign with its actions as `GET /campaigns/:id` returns them: the full
 * detail shape, never the sparse rows a campaigns-list sideload carries.
 * Stored under `campaignKeys.workflowDetail` for the workflow canvas.
 */
export type CampaignWorkflowDetail = Pick<ParsedCampaignModel, 'campaign' | 'actions'>;

export function toCampaignWorkflowDetail(model: ParsedCampaignModel): CampaignWorkflowDetail {
  return { campaign: model.campaign, actions: model.actions };
}

/**
 * The generated CampaignModel-shaped responses (`PUT /campaigns/:id` and
 * `GET /campaigns/:id`) type `campaign` as optional and the sideload
 * arrays as non-null, but the wire omits whichever clusters the campaign
 * hasn't pulled in (e.g. an action-less draft has no `actions`). Widen
 * the arrays back to optional so the `?? []` guards are honest, and
 * require an explicit `campaign` null-check at parse time.
 */
type GeneratedCampaignModel = Omit<CampaignsCampaignModel, 'actions' | 'tags' | 'subscription_topics'> & {
  actions?: CampaignsCampaignModel['actions'];
  tags?: CampaignsCampaignModel['tags'];
  subscription_topics?: CampaignsCampaignModel['subscription_topics'];
  /** On `GET /campaigns/:id`'s response only, which the shared model type leaves out. */
  most_recent_trigger?: BroadcastTriggerWire;
};

export function parseCampaignModel(model: GeneratedCampaignModel): ParsedCampaignModel {
  if (model.campaign == null) {
    throw new Error('missing campaign in campaign model response');
  }
  const parsed: ParsedCampaignModel = {
    campaign: parseCampaign(model.campaign),
    actions: (model.actions ?? []).map(parseAction),
    tags: (model.tags ?? []).map(parseTag),
    subscription_topics: (model.subscription_topics ?? []).map((topic) => parseSubscriptionTopic(topic)),
  };
  if (model.most_recent_trigger != null) {
    parsed.most_recent_trigger = parseBroadcastTrigger(model.most_recent_trigger);
  }
  return parsed;
}

/**
 * Fan a CampaignModel envelope across the cache. Single source of truth
 * for "I just got a CampaignModel-shaped response, propagate it correctly."
 *
 * **Normalized.** Each entity is written exactly once — to its own detail
 * cache slot. Relation views (e.g. `campaign.actions`) are NOT stored as
 * separate cache slots; they're derived at read time by following FKs
 * through `useEntitiesByIds(detailSlotForId, fkArray)`. Same data
 * appears in only one place; updates propagate everywhere automatically.
 *
 * What this does write:
 *   - `campaign.keys.detail(wsId, campaignId)` — the campaign itself.
 *   - `action.keys.detail(wsId, actionId)` — each action.
 *   - `tag.keys.detail(wsId, tagId)` — each tag.
 *   - `subscription_topic.keys.detail(wsId, topicId)` — each topic.
 *   - `campaignKeys.workflowDetail(wsId, campaignId)` — the campaign with
 *     its detail-shaped actions, for the workflow canvas. Action detail
 *     slots are shared with the sparse campaigns-list sideload, so the
 *     canvas reads this slot instead of following `workflow_action_ids`.
 *
 * What relation reads do:
 *   - `useCampaignActions(campaign)` reads `campaign.workflow_action_ids`
 *     and resolves each id via `useEntitiesByIds(...)`.
 *   - `useCampaignTags(campaign)` reads `campaign.tag_ids` similarly.
 *   - `useCampaignsForTag(tagId)` derives via `useCampaignsCached({ select })`.
 *   - `useCampaignForAction(action)` follows `action.campaign_id`.
 *
 * Trade-off: there's no addressable cache slot for "this campaign's
 * actions" specifically — invalidations work at the entity-detail level
 * instead. Mutations that update an action setQueryData its detail slot;
 * every relation view that references it re-renders through TanStack
 * Query's per-query notification. Net less plumbing, fewer slots to
 * keep in sync.
 */
export function populateCampaignModel(qc: QueryClient, workspaceId: number, model: ParsedCampaignModel): void {
  qc.setQueryData(campaignKeys.detail(workspaceId, model.campaign.id), model.campaign);
  // No relation slots — relation views (campaign.actions, campaign.tags,
  // campaign.subscription_topics) are derived at read time by following
  // FK arrays into the detail caches. See `useCampaignActions(campaign)`
  // etc. in `campaign/queries.ts`.
  populateDetailSlots(qc, model.actions, (a) => actionKeys.detail(workspaceId, a.id));
  populateDetailSlots(qc, model.tags, (t) => tagKeys.detail(workspaceId, t.id));
  populateDetailSlots(qc, model.subscription_topics, (st) => subscriptionTopicKeys.detail(workspaceId, st.id));
  qc.setQueryData(campaignKeys.workflowDetail(workspaceId, model.campaign.id), toCampaignWorkflowDetail(model));
}

/* ------------------------------------------------------------------------- */
/* List response                                                              */
/* ------------------------------------------------------------------------- */

export interface ParsedCampaignsListResponse {
  campaigns: readonly Campaign[];
  actions: readonly Action[];
  tags: readonly Tag[];
  subscription_topics: readonly SubscriptionTopic[];
  meta?: CampaignsListCampaignsResponse['meta'];
}

/**
 * The generated list response types the sideload arrays as non-null, but
 * the wire omits whichever the request excluded (`excludeActions`,
 * `excludeTags`, …). Widen them to optional so the `?? []` guards below
 * are honest — same treatment as {@link GeneratedCampaignModel}.
 */
type GeneratedCampaignsListResponse = Omit<
  CampaignsListCampaignsResponse,
  'actions' | 'tags' | 'subscription_topics'
> & {
  actions?: CampaignsListCampaignsResponse['actions'];
  tags?: CampaignsListCampaignsResponse['tags'];
  subscription_topics?: CampaignsListCampaignsResponse['subscription_topics'];
};

export function parseCampaignsListResponse(raw: GeneratedCampaignsListResponse): ParsedCampaignsListResponse {
  return {
    campaigns: raw.campaigns.map(parseCampaign),
    actions: (raw.actions ?? []).map(parseAction),
    tags: (raw.tags ?? []).map(parseTag),
    subscription_topics: (raw.subscription_topics ?? []).map((topic) => parseSubscriptionTopic(topic)),
    meta: raw.meta,
  };
}

/**
 * Cluster propagation for the LIST response. Writes detail caches only;
 * relation views are derived at read time from the FK arrays each
 * campaign carries (`workflow_action_ids`, `tag_ids`, `subscription_topic_id`).
 *
 * Does not write `campaignKeys.workflowDetail`: the list's action rows are
 * the sparse `ActionForCampaignsList` shape, which the workflow canvas must
 * never lay out.
 */
export function populateCampaignsList(
  qc: QueryClient,
  workspaceId: number,
  response: ParsedCampaignsListResponse,
): void {
  populateDetailSlots(qc, response.campaigns, (c) => campaignKeys.detail(workspaceId, c.id));
  populateDetailSlots(qc, response.actions, (a) => actionKeys.detail(workspaceId, a.id));
  populateDetailSlots(qc, response.tags, (t) => tagKeys.detail(workspaceId, t.id));
  populateDetailSlots(qc, response.subscription_topics, (st) => subscriptionTopicKeys.detail(workspaceId, st.id));
}

/**
 * Last-write-wins cache write for each sideloaded entity.
 *
 * Deliberately does **not** `cancelQueries` first the way the optimistic
 * mutations do. The list response is the freshest server snapshot for
 * every entity it contains — if a concurrent detail fetch is in flight,
 * letting it land later is fine (it would only carry equally fresh or
 * fresher data, since both queries hit the same backend). The race the
 * optimistic-delete cancel guards against is *client-state vs. server*,
 * which doesn't apply here.
 */
function populateDetailSlots<T>(qc: QueryClient, items: readonly T[], keyFor: (item: T) => readonly unknown[]): void {
  for (const item of items) qc.setQueryData(keyFor(item), item);
}
